import { useState, useCallback, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslations } from "next-intl"
import { toast } from "sonner"
import { Banknote, RefreshCw, Brain, Euro, Calendar, Building2, Layers, CheckCircle, Sparkles, SearchCode, X } from "lucide-react"
import { DashboardLayout } from "@/components/common/DashboardLayout"
import { DataTable, ColumnDef } from "@/components/common/DataTable"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { FilterSheet } from "@/components/ui/filter-sheet"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { api } from "@/lib/apiService"
import { useLocaleStore } from "@/stores/localeStore"
import { formatCurrency, formatDate, formatDateRelative, getRelevanceBgColor } from "@/lib/utils"
import type { Grant, GrantListResponse, GrantFilterOptionsResponse, GrantAiSearchResponse } from "@/types/grant.types"
import type { FilterGroupDefinition, FilterSelections } from "@/types/filters"

interface GrantFilters {
	search: string
	nivel1: string[]
	nivel3: string[]
	minBudget: string
	maxBudget: string
	deadlineFrom: string
	deadlineTo: string
	receivedFrom: string
	receivedTo: string
	openOnly: boolean
	mrr: boolean
	onlyRelevant: boolean
}

const DEFAULT_FILTERS: GrantFilters = {
	search: "",
	nivel1: [],
	nivel3: [],
	minBudget: "",
	maxBudget: "",
	deadlineFrom: "",
	deadlineTo: "",
	receivedFrom: "",
	receivedTo: "",
	openOnly: false,
	mrr: false,
	onlyRelevant: false,
}

function countActiveFilters(f: GrantFilters): number {
	let n = 0
	if (f.nivel1.length > 0) n++
	if (f.nivel3.length > 0) n++
	if (f.minBudget || f.maxBudget) n++
	if (f.deadlineFrom || f.deadlineTo) n++
	if (f.receivedFrom || f.receivedTo) n++
	if (f.openOnly) n++
	if (f.mrr) n++
	return n
}

function filtersToSelections(f: GrantFilters): FilterSelections {
	return {
		nivel1: f.nivel1,
		nivel3: f.nivel3,
		budget: [f.minBudget, f.maxBudget],
		deadline: [f.deadlineFrom, f.deadlineTo],
		received: [f.receivedFrom, f.receivedTo],
		flags: [
			...(f.openOnly ? ["openOnly"] : []),
			...(f.mrr ? ["mrr"] : []),
		],
	}
}

function selectionsToFilters(sel: FilterSelections, current: GrantFilters): GrantFilters {
	const flags = sel.flags ?? []
	return {
		...current,
		nivel1: sel.nivel1 ?? [],
		nivel3: sel.nivel3 ?? [],
		minBudget: sel.budget?.[0] ?? "",
		maxBudget: sel.budget?.[1] ?? "",
		deadlineFrom: sel.deadline?.[0] ?? "",
		deadlineTo: sel.deadline?.[1] ?? "",
		receivedFrom: sel.received?.[0] ?? "",
		receivedTo: sel.received?.[1] ?? "",
		openOnly: flags.includes("openOnly"),
		mrr: flags.includes("mrr"),
	}
}

function RelevanceBadge({ score, reason }: { score?: number | null; reason?: string | null }) {
	if (score == null) return <span className="text-xs text-muted-foreground">—</span>
	const badge = (
		<span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${getRelevanceBgColor(score)}`}>
			<Brain className="h-3 w-3" />
			{score}%
		</span>
	)
	if (!reason) return badge
	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>{badge}</TooltipTrigger>
				<TooltipContent side="left" className="max-w-xs text-xs">{reason}</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	)
}

function StatusBadge({ grant, t }: { grant: Grant; t: ReturnType<typeof useTranslations> }) {
	const isOpen = grant.abierto === true ||
		(grant.fecha_fin_solicitud && new Date(grant.fecha_fin_solicitud) >= new Date())
	if (isOpen) {
		return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-green-50 text-green-700 border-green-200">{t("grants.badges.open")}</span>
	}
	return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-gray-50 text-gray-600 border-gray-200">{t("grants.badges.closed")}</span>
}

export default function GrantsPage() {
	const t = useTranslations()
	const navigate = useNavigate()
	const { locale } = useLocaleStore()

	const [grants, setGrants] = useState<Grant[]>([])
	const [total, setTotal] = useState(0)
	const [page, setPage] = useState(1)
	const [pages, setPages] = useState(1)
	const [isLoading, setIsLoading] = useState(true)
	const [isSyncing, setIsSyncing] = useState(false)
	const [isAiSearching, setIsAiSearching] = useState(false)
	const [isAiMode, setIsAiMode] = useState(false)
	const [aiQuery, setAiQuery] = useState("")
	const [filters, setFilters] = useState<GrantFilters>(DEFAULT_FILTERS)
	const [filterOpen, setFilterOpen] = useState(false)
	const [sortBy, setSortBy] = useState("fecha_recepcion")
	const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")
	const [filterOptions, setFilterOptions] = useState<GrantFilterOptionsResponse["data"] | null>(null)

	useEffect(() => {
		api.get<GrantFilterOptionsResponse>("/grants/filter-options")
			.then((res) => setFilterOptions(res.data))
			.catch(() => {})
	}, [])

	const filterSelections = useMemo(() => filtersToSelections(filters), [filters])

	const fetchGrants = useCallback(async (currentFilters: GrantFilters, currentPage: number) => {
		setIsLoading(true)
		setIsAiMode(false)
		try {
			const params = new URLSearchParams()
			params.set("page", String(currentPage))
			params.set("limit", "20")
			params.set("sortBy", sortBy)
			params.set("sortOrder", sortOrder)

			if (currentFilters.search) params.set("search", currentFilters.search)
			if (currentFilters.minBudget) params.set("minBudget", currentFilters.minBudget)
			if (currentFilters.maxBudget) params.set("maxBudget", currentFilters.maxBudget)
			if (currentFilters.openOnly) params.set("abierto", "true")
			if (currentFilters.mrr) params.set("mrr", "true")
			if (currentFilters.onlyRelevant) params.set("onlyRelevant", "true")
			if (currentFilters.deadlineFrom) params.set("deadlineFrom", currentFilters.deadlineFrom)
			if (currentFilters.deadlineTo) params.set("deadlineTo", currentFilters.deadlineTo)
			if (currentFilters.receivedFrom) params.set("receivedFrom", currentFilters.receivedFrom)
			if (currentFilters.receivedTo) params.set("receivedTo", currentFilters.receivedTo)
			currentFilters.nivel1.forEach((s) => params.append("nivel1", s))
			currentFilters.nivel3.forEach((s) => params.append("nivel3", s))

			const res = await api.get<GrantListResponse>(`/grants?${params.toString()}`)
			setGrants(res.data)
			setTotal(res.total)
			setPages(res.pages)
		} catch {
			toast.error(t("grants.loadError"))
		} finally {
			setIsLoading(false)
		}
	}, [sortBy, sortOrder, t])

	useEffect(() => {
		fetchGrants(filters, page)
	}, [fetchGrants, filters, page])

	const handleAiSearch = async () => {
		if (!aiQuery.trim() || aiQuery.length < 3) return
		setIsAiSearching(true)
		try {
			const res = await api.post<GrantAiSearchResponse>("/grants/ai-search", { query: aiQuery, limit: 30 })
			setGrants(res.data)
			setTotal(res.data.length)
			setPages(1)
			setIsAiMode(true)
			if (res.extractedFilters && Object.keys(res.extractedFilters).length > 0) {
				toast.info(`IA detectó: ${JSON.stringify(res.extractedFilters)}`, { duration: 4000 })
			}
		} catch {
			toast.error(t("grants.loadError"))
		} finally {
			setIsAiSearching(false)
		}
	}

	const handleApplyFilters = (selections: FilterSelections) => {
		setFilters(selectionsToFilters(selections, filters))
		setPage(1)
	}

	const handleClearFilters = () => {
		setFilters(DEFAULT_FILTERS)
		setPage(1)
	}

	const handleSync = async () => {
		if (isSyncing) return
		setIsSyncing(true)
		try {
			await api.post("/grants/sync/run", {})
			toast.success(t("grants.syncStarted"))
			setTimeout(() => fetchGrants(filters, page), 3000)
		} catch {
			toast.error(t("grants.loadError"))
		} finally {
			setTimeout(() => setIsSyncing(false), 5000)
		}
	}

	const handleSort = (col: string, order: "asc" | "desc") => {
		setSortBy(col)
		setSortOrder(order)
		setPage(1)
	}

	const activeFilterCount = countActiveFilters(filters)

	const filterGroups: FilterGroupDefinition[] = useMemo(() => [
		{
			key: "nivel1",
			label: t("grants.filterPanel.nivel1"),
			icon: Layers,
			type: "multi-select" as const,
			options: (filterOptions?.nivel1 || []).map((s) => ({ value: s, label: s })),
		},
		{
			key: "nivel3",
			label: t("grants.filterPanel.nivel3"),
			icon: Building2,
			type: "multi-select" as const,
			options: (filterOptions?.nivel3 || []).slice(0, 500).map((s) => ({ value: s, label: s })),
		},
		{
			key: "budget",
			label: t("grants.filterPanel.budget"),
			icon: Euro,
			type: "number-range" as const,
			options: [],
			numberRangeLabels: {
				min: t("grants.filterPanel.minBudget"),
				max: t("grants.filterPanel.maxBudget"),
				minPlaceholder: "0",
				maxPlaceholder: "∞",
			},
		},
		{
			key: "deadline",
			label: t("grants.filterPanel.deadline"),
			icon: Calendar,
			type: "date-range" as const,
			options: [],
		},
		{
			key: "received",
			label: t("grants.filterPanel.received"),
			icon: Calendar,
			type: "date-range" as const,
			options: [],
		},
		{
			key: "flags",
			label: t("grants.filterPanel.openOnly"),
			icon: CheckCircle,
			type: "multi-select" as const,
			options: [
				{ value: "openOnly", label: t("grants.filterPanel.openOnly") },
				{ value: "mrr", label: t("grants.filterPanel.mrr") },
			],
		},
	], [t, filterOptions])

	const columns: ColumnDef<Grant>[] = [
		{
			id: "descripcion",
			header: t("grants.columns.description"),
			cell: (row) => (
				<div className="max-w-md">
					<p className="font-medium text-sm text-gray-900 line-clamp-2 leading-snug">
						{row.descripcion || "—"}
					</p>
					<p className="text-xs text-muted-foreground mt-0.5">BDNS {row.codigo_bdns}</p>
					{row.nivel3 && (
						<p className="text-xs text-muted-foreground truncate max-w-xs">{row.nivel3}</p>
					)}
				</div>
			),
		},
		{
			id: "presupuesto_total",
			header: t("grants.columns.budget"),
			sortable: true,
			className: "text-right whitespace-nowrap",
			headerClassName: "text-right",
			cell: (row) => (
				<span className="text-sm font-mono font-medium">{formatCurrency(row.presupuesto_total)}</span>
			),
		},
		{
			id: "status",
			header: t("grants.columns.status"),
			cell: (row) => (
				<div className="flex items-center gap-1.5">
					<StatusBadge grant={row} t={t} />
					{row.mrr && (
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border bg-blue-50 text-blue-700 border-blue-200">
										<Sparkles className="h-2.5 w-2.5 mr-0.5" />
										MRR
									</span>
								</TooltipTrigger>
								<TooltipContent>{t("grants.badges.mrr")}</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					)}
				</div>
			),
		},
		{
			id: "fecha_recepcion",
			header: t("grants.columns.received"),
			sortable: true,
			cell: (row) => <span className="text-sm whitespace-nowrap">{formatDate(row.fecha_recepcion)}</span>,
		},
		{
			id: "fecha_fin_solicitud",
			header: t("grants.columns.deadline"),
			sortable: true,
			cell: (row) => (
				<div className="text-sm whitespace-nowrap">
					<p>{formatDate(row.fecha_fin_solicitud)}</p>
					{row.fecha_fin_solicitud && (
						<p className="text-xs text-muted-foreground">{formatDateRelative(row.fecha_fin_solicitud, locale)}</p>
					)}
				</div>
			),
		},
		{
			id: "ai_relevance_score",
			header: t("grants.columns.score"),
			sortable: true,
			cell: (row) => <RelevanceBadge score={row.ai_relevance_score} reason={row.ai_relevance_reason} />,
		},
	]

	return (
		<DashboardLayout>
			{/* AI Search bar */}
			<div className="bg-gradient-to-r from-[#2d5c3f]/5 via-[#3d7a4f]/5 to-[#4a9960]/5 border border-[#2d5c3f]/10 rounded-xl p-4">
				<div className="flex items-center gap-2 mb-3">
					<SearchCode className="h-4 w-4 text-[#2D8A5E]" />
					<span className="text-sm font-semibold text-[#2d5c3f]">{t("grants.aiSearch")}</span>
				</div>
				<div className="flex gap-2">
					<Input
						value={aiQuery}
						onChange={(e) => setAiQuery(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && handleAiSearch()}
						placeholder={t("grants.aiSearchPlaceholder")}
						className="flex-1 bg-white border-[#2d5c3f]/20 focus:border-[#2D8A5E] focus:ring-[#2D8A5E]"
					/>
					<Button
						onClick={handleAiSearch}
						disabled={isAiSearching || aiQuery.length < 3}
						className="bg-[#2D8A5E] hover:bg-[#2d5c3f] text-white whitespace-nowrap"
					>
						{isAiSearching ? (
							<><RefreshCw className="h-4 w-4 mr-2 animate-spin" />{t("grants.aiSearching")}</>
						) : (
							<><Brain className="h-4 w-4 mr-2" />{t("grants.aiSearchButton")}</>
						)}
					</Button>
					{isAiMode && (
						<Button variant="outline" size="icon" onClick={() => { setAiQuery(""); setPage(1); fetchGrants(filters, 1) }}>
							<X className="h-4 w-4" />
						</Button>
					)}
				</div>

				<div className="mt-3 flex items-center gap-2">
					<Checkbox
						id="onlyRelevant"
						checked={filters.onlyRelevant}
						onCheckedChange={(checked) => {
							setFilters((p) => ({ ...p, onlyRelevant: !!checked }))
							setPage(1)
						}}
					/>
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<label htmlFor="onlyRelevant" className="text-sm font-medium text-[#2d5c3f] cursor-pointer select-none flex items-center gap-1.5">
									<Brain className="h-3.5 w-3.5" />
									{t("grants.onlyRelevant")}
								</label>
							</TooltipTrigger>
							<TooltipContent side="right" className="max-w-xs text-xs">{t("grants.onlyRelevantTooltip")}</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</div>
			</div>

			<DataTable
				data={grants}
				columns={columns}
				isLoading={isLoading}
				headerExtra={
					<Button variant="outline" size="sm" onClick={handleSync} disabled={isSyncing}>
						<RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? "animate-spin" : ""}`} />
						{isSyncing ? t("grants.syncing") : t("grants.sync")}
					</Button>
				}
				title={
					isAiMode
						? `${t("grants.title")} — ${t("grants.aiSearchResults")}`
						: `${t("grants.title")} ${!isLoading ? `(${total.toLocaleString()})` : ""}`
				}
				titleIcon={<Banknote className="h-5 w-5 text-[#2D8A5E]" />}
				searchPlaceholder={t("grants.search")}
				onSearchChange={(val) => {
					setFilters((prev) => ({ ...prev, search: val }))
					setPage(1)
				}}
				searchValue={filters.search}
				emptyMessage={t("grants.empty")}
				emptyIcon={<Banknote className="h-12 w-12 text-muted-foreground/30" />}
				onRowClick={(row) => navigate(`/grants/${row.id}`)}
				sortBy={sortBy}
				sortOrder={sortOrder}
				onSort={handleSort}
				pagination={
					pages > 1
						? { page, limit: 20, total, pages, onPageChange: setPage }
						: undefined
				}
				filterConfig={{
					activeCount: activeFilterCount,
					onOpen: () => setFilterOpen(true),
					label: t("grants.filters"),
				}}
				getRowId={(row) => String(row.id)}
			/>

			<FilterSheet
				open={filterOpen}
				onOpenChange={setFilterOpen}
				title={t("grants.filterPanel.title")}
				groups={filterGroups}
				selections={filterSelections}
				onApply={handleApplyFilters}
				onClearAll={handleClearFilters}
				applyLabel={t("grants.filterPanel.apply")}
				clearAllLabel={t("grants.filterPanel.clear")}
				dateFromLabel={t("grants.filterPanel.from")}
				dateToLabel={t("grants.filterPanel.to")}
				selectPlaceholder={t("common.selectPlaceholder")}
				searchPlaceholder={t("common.searchPlaceholder")}
				noResultsLabel={t("common.noResults")}
			/>
		</DashboardLayout>
	)
}
